import { TagDto, UserSharedType } from '../../ContentPermissions/ContentPermission.types';
import {
  CustomDashboardDto,
  DashboardReportPermission,
  DashboardReportPermissionSetLink,
  PermissionType,
  publicId,
  UserPermissionType,
} from '../types';

export type PermissionSetEntry = { id: string; name: string };

export const TAG_MANAGED_PERMISSION_SET_TOOLTIP = "Can't remove this permission set, managed through a tag.";

export const splitByIds = (values: string[], idsInGroup: string[]) => ({
  matching: values.filter(v => idsInGroup.includes(v)),
  rest: values.filter(v => !idsInGroup.includes(v)),
});

export const withDisabledIds = <T extends { value: string; disabled?: boolean; tooltip?: string }>(
  options: T[],
  disabledIds: string[],
  tooltippedIds?: string[],
  tooltip?: string
): T[] =>
  options.map(option => {
    if (tooltippedIds?.includes(option.value)) return { ...option, disabled: true, ...(tooltip && { tooltip }) };
    if (disabledIds.includes(option.value)) return { ...option, disabled: true };
    return option;
  });

const getTagDerivedPermissionSets = (links: DashboardReportPermissionSetLink[], tags: TagDto[], appliedTagNames: string[]) => {
  const directEditorIds = new Set(links.filter(l => l.sharedType === UserPermissionType.editor).map(l => l.permissionSetId));
  const directViewerIds = new Set(links.filter(l => l.sharedType === UserPermissionType.viewer).map(l => l.permissionSetId));
  const appliedTagNamesSet = new Set(appliedTagNames);
  const tagEditors = new Map<string, string>();
  const tagViewers = new Map<string, string>();
  tags
    .filter(t => appliedTagNamesSet.has(t.name))
    .flatMap(t => t.permissionSets)
    .forEach(ps => {
      const { permissionSetId: id, permissionSetName: name } = ps;
      if (ps.sharedType === UserSharedType.Editor && !directEditorIds.has(id)) tagEditors.set(id, name);
      else if (ps.sharedType === UserSharedType.Viewer && !directViewerIds.has(id) && !directEditorIds.has(id) && !tagEditors.has(id))
        tagViewers.set(id, name);
    });
  return {
    editors: [...tagEditors.entries()].map(([id, name]) => ({ id, name })) as PermissionSetEntry[],
    viewers: [...tagViewers.entries()].map(([id, name]) => ({ id, name })) as PermissionSetEntry[],
  };
};

export const getTagDerivedPermissionSetIds = (
  links: DashboardReportPermissionSetLink[],
  tags: TagDto[],
  appliedTagNames: string[]
): { tagEditorIds: string[]; tagViewerIds: string[] } => {
  const { editors, viewers } = getTagDerivedPermissionSets(links, tags, appliedTagNames);
  return { tagEditorIds: editors.map(e => e.id), tagViewerIds: viewers.map(v => v.id) };
};

const allViewerPermissionSetIds = (
  viewerIds: Iterable<string>,
  editorIds: Iterable<string>,
  tagEditorIds: string[],
  tagViewerIds: string[]
): string[] => [...new Set([...viewerIds, ...editorIds, ...tagEditorIds, ...tagViewerIds])];

export const getNewTagPermissionSetEntries = (
  initData: Partial<CustomDashboardDto>,
  newData: Partial<CustomDashboardDto>,
  tags: TagDto[]
): { editors: PermissionSetEntry[]; viewers: PermissionSetEntry[] } => {
  const initial = getTagDerivedPermissionSets(initData.dashboardReportPermission?.permissionSets ?? [], tags, initData.tags ?? []);
  const initialIds = new Set([...initial.editors.map(e => e.id), ...initial.viewers.map(v => v.id)]);
  const current = getTagDerivedPermissionSets(newData.dashboardReportPermission?.permissionSets ?? [], tags, newData.tags ?? []);
  return {
    editors: current.editors.filter(e => !initialIds.has(e.id)),
    viewers: current.viewers.filter(v => !initialIds.has(v.id)),
  };
};

export const buildViewersSelectValue = (
  isPublic: boolean,
  userIds: string[],
  permissionSetIds: { viewerIds: Iterable<string>; editorIds: Iterable<string>; tagEditorIds: string[]; tagViewerIds: string[] } | null
): string[] => {
  if (isPublic) return [publicId];
  return [
    ...userIds,
    ...(permissionSetIds
      ? allViewerPermissionSetIds(
          permissionSetIds.viewerIds,
          permissionSetIds.editorIds,
          permissionSetIds.tagEditorIds,
          permissionSetIds.tagViewerIds
        )
      : []),
  ];
};

export const buildEditorsSelectValue = (
  isPublic: boolean,
  editorUserIds: string[],
  permissionSetIds: { editorIds: Iterable<string>; tagEditorIds: string[] } | null
): string[] => {
  if (isPublic) return [publicId];
  return [...editorUserIds, ...(permissionSetIds ? [...new Set([...permissionSetIds.editorIds, ...permissionSetIds.tagEditorIds])] : [])];
};

export const setPermissionSetsLevel = (
  links: DashboardReportPermissionSetLink[],
  allPermissionSetIds: string[],
  selectedIds: string[],
  sharedType: UserPermissionType
): DashboardReportPermissionSetLink[] => {
  const untouched = links.filter(l => !allPermissionSetIds.includes(l.permissionSetId));
  const keptOtherLevel = links.filter(
    l => allPermissionSetIds.includes(l.permissionSetId) && l.sharedType !== sharedType && !selectedIds.includes(l.permissionSetId)
  );
  const updated = selectedIds.map(permissionSetId => ({ permissionSetId, sharedType }));
  return [...untouched, ...keptOtherLevel, ...updated];
};

export const getAttentionMessage = (isEnabled: boolean, hasTags: boolean, hasEditorPermissionSets: boolean) => {
  if (!isEnabled) return '';
  if (hasTags) return 'Access is assigned automatically based on the selected tag\u2019s permissions.';
  if (hasEditorPermissionSets)
    return (
      <>
        If a user has multiple access levels, directly or via permission sets, <strong>Editor</strong> access overrides the others.
      </>
    );
  return '';
};

export const getPermissionType = (newDashboardReportPermission: DashboardReportPermission) => {
  if (newDashboardReportPermission.editors.includes(publicId)) return PermissionType.publicEdit;
  if (newDashboardReportPermission.viewers.includes(publicId)) return PermissionType.publicView;
  if (
    newDashboardReportPermission.editors.length ||
    newDashboardReportPermission.viewers.length ||
    newDashboardReportPermission.permissionSets?.length
  )
    return PermissionType.shared;

  return PermissionType.private;
};
